import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DataService } from 'src/app/services/data.service';
import { GamedataDownloadService } from 'src/app/build-gamedata/services/gamedata-download.service';
import { LoggerService } from 'src/app/common/services/logger.service';

interface StringDiff {
  prefix: string;
  removed: string;
  added: string;
  suffix: string;
}

@Component({
  selector: 'app-gamedata-diff-view-v2',
  templateUrl: './gamedata-diff-view-v2.component.html',
  styleUrls: ['./gamedata-diff-view-v2.component.sass'],
})
export class GamedataDiffViewV2Component implements OnInit {
  selectedDiff: any = null;
  isLoading = true;
  params = '';
  buildVersionParam = '';
  envParam = '';
  showRawData = false;
  copySuccess = false;

  statsData = { tablesChanged: 0, recordsCreated: 0, recordsUpdated: 0, recordsDeleted: 0 };

  tableOverview: any[] = [];
  recordDetailsCache = new Map<string, any[]>();

  // Sidebar
  selectedTableName = '';
  sidebarSearch = '';

  // Main view filters
  rowSearch = '';
  activeFilters = new Set<string>(['added', 'deleted', 'updated']);

  // Rows + pagination
  filteredRows: any[] = [];
  totalRows = 0;
  currentPage = 1;
  pageSize = 25;
  pageSizeOptions = [25, 50, 100];

  // Detail panel
  selectedRecord: any = null;
  selectedRecordChanges: any[] = [];

  constructor(
    private dataService: DataService,
    private route: ActivatedRoute,
    private router: Router,
    private gamedataDownloadService: GamedataDownloadService,
    private logger: LoggerService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.route.queryParams.subscribe(params => {
      if (params.id) {
        this.params = params.id;
      } else if (params.buildVersion) {
        this.buildVersionParam = params.buildVersion;
        this.envParam = params.env || 'dev';
      }
    });

    if (!this.params && !this.buildVersionParam) {
      this.isLoading = false;
      return;
    }

    try {
      if (this.params) {
        this.selectedDiff = await this.dataService.getDocumentAsync('GamedataDiff', {
          query: { id: this.params },
          autopopulate: true,
          virtuals: true,
        });
      } else if (this.buildVersionParam) {
        this.selectedDiff = await this.dataService.getDocumentAsync('GamedataDiff', {
          query: { buildVersion: this.buildVersionParam, environment: this.envParam },
          autopopulate: true,
          virtuals: true,
        });
      }

      if (this.selectedDiff?.diff?.tables) {
        this.processSelectedDiff();
      }
    } catch (error) {
      this.logger.error('Error fetching gamedata diff:', error);
    } finally {
      this.isLoading = false;
    }
  }

  processSelectedDiff(): void {
    if (!this.selectedDiff?.diff?.tables) return;

    const tables = this.selectedDiff.diff.tables;
    this.tableOverview = [];
    this.recordDetailsCache.clear();

    let totalTablesChanged = 0;
    let totalRecordsCreated = 0;
    let totalRecordsUpdated = 0;
    let totalRecordsDeleted = 0;

    Object.keys(tables).forEach(tableName => {
      const td = tables[tableName];
      if (td.stats) {
        totalTablesChanged++;
        totalRecordsCreated += td.stats.added_count || 0;
        totalRecordsUpdated += td.stats.updated_count || 0;
        totalRecordsDeleted += td.stats.deleted_count || 0;
        this.tableOverview.push({
          name: tableName,
          createdCount: td.stats.added_count || 0,
          editedCount: td.stats.updated_count || 0,
          deletedCount: td.stats.deleted_count || 0,
          cellsChanged: td.stats.cells_changed || 0,
          isTruncated: td.is_truncated || false,
          rawTableData: td,
          updatedRows: td.updated_rows || [],
          addedRows: td.added_rows || [],
          deletedRows: td.deleted_rows || [],
        });
      }
    });

    this.tableOverview.sort((a, b) => this.getTotalChanges(b) - this.getTotalChanges(a));

    this.statsData = {
      tablesChanged: totalTablesChanged,
      recordsCreated: totalRecordsCreated,
      recordsUpdated: totalRecordsUpdated,
      recordsDeleted: totalRecordsDeleted,
    };

    if (this.tableOverview.length > 0) {
      this.selectTable(this.tableOverview[0].name);
    }
  }

  // ==================== Sidebar ====================

  get filteredTableList(): any[] {
    if (!this.sidebarSearch.trim()) return this.tableOverview;
    const q = this.sidebarSearch.toLowerCase();
    return this.tableOverview.filter(t => t.name.toLowerCase().includes(q));
  }

  get selectedTableOverview(): any {
    return this.tableOverview.find(t => t.name === this.selectedTableName) || null;
  }

  getTotalChanges(table: any): number {
    return (table.createdCount || 0) + (table.editedCount || 0) + (table.deletedCount || 0);
  }

  getImpactLabel(table: any): string {
    const n = this.getTotalChanges(table);
    if (n >= 20) return 'High Impact';
    if (n >= 5) return 'Medium Impact';
    return 'Low Impact';
  }

  getImpactClass(table: any): string {
    const n = this.getTotalChanges(table);
    if (n >= 20) return 'impact-high';
    if (n >= 5) return 'impact-medium';
    return 'impact-low';
  }

  getImpactReason(table: any): string {
    const parts: string[] = [];
    if (table.createdCount > 0) parts.push(`${table.createdCount} record${table.createdCount !== 1 ? 's' : ''} added`);
    if (table.deletedCount > 0) parts.push(`${table.deletedCount} record${table.deletedCount !== 1 ? 's' : ''} deleted`);
    if (table.editedCount > 0) parts.push(`${table.editedCount} record${table.editedCount !== 1 ? 's' : ''} updated`);
    if (table.cellsChanged > 0) parts.push(`${table.cellsChanged} cell${table.cellsChanged !== 1 ? 's' : ''} changed`);
    if (table.createdCount === 0 && table.deletedCount === 0) parts.push('no structural changes');
    return parts.join(' · ');
  }

  selectTable(name: string): void {
    this.selectedTableName = name;
    this.rowSearch = '';
    this.currentPage = 1;
    this.selectedRecord = null;
    this.selectedRecordChanges = [];

    if (!this.recordDetailsCache.has(name)) {
      this.recordDetailsCache.set(name, this.computeRecordDetailsForTable(name));
    }

    this.updateFilteredRows();

    // Auto-open detail panel when the table has exactly one unique record
    const allRows = this.recordDetailsCache.get(name) || [];
    const uniqueIds = new Set(allRows.map((r: any) => String(r.recordId)));
    if (uniqueIds.size === 1 && allRows.length > 0) {
      this.selectRecord(allRows[0]);
    }
  }

  // ==================== Filters ====================

  toggleFilter(type: string): void {
    if (this.activeFilters.has(type)) {
      this.activeFilters.delete(type);
    } else {
      this.activeFilters.add(type);
    }
    this.currentPage = 1;
    this.updateFilteredRows();
  }

  isFilterActive(type: string): boolean {
    return this.activeFilters.has(type);
  }

  onRowSearchChange(): void {
    this.currentPage = 1;
    this.updateFilteredRows();
  }

  private updateFilteredRows(): void {
    const allRows = this.recordDetailsCache.get(this.selectedTableName) || [];
    const q = this.rowSearch.trim().toLowerCase();

    const filtered = allRows.filter(row => {
      if (!this.activeFilters.has(row.changeType)) return false;
      if (q) {
        const id = String(row.recordId).toLowerCase();
        const field = row.fieldName.toLowerCase();
        if (!id.includes(q) && !field.includes(q)) return false;
      }
      return true;
    });

    this.totalRows = filtered.length;
    const start = (this.currentPage - 1) * this.pageSize;
    this.filteredRows = filtered.slice(start, start + this.pageSize);
  }

  // ==================== Pagination ====================

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalRows / this.pageSize));
  }

  get pageStart(): number {
    return this.totalRows === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get pageEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRows);
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updateFilteredRows();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updateFilteredRows();
    }
  }

  onPageSizeChange(size: number): void {
    this.pageSize = +size;
    this.currentPage = 1;
    this.updateFilteredRows();
  }

  // ==================== Detail Panel ====================

  selectRecord(change: any): void {
    this.selectedRecord = change;
    this.selectedRecordChanges = this.getChangesForRecord(change.recordId);
  }

  closeDetailPanel(): void {
    this.selectedRecord = null;
    this.selectedRecordChanges = [];
  }

  getChangesForRecord(recordId: any): any[] {
    const allRows = this.recordDetailsCache.get(this.selectedTableName) || [];
    return allRows.filter(r => String(r.recordId) === String(recordId));
  }

  // ==================== Delta ====================

  calculateDelta(oldVal: any, newVal: any): number | null {
    const o = parseFloat(String(oldVal));
    const n = parseFloat(String(newVal));
    return !isNaN(o) && !isNaN(n) ? n - o : null;
  }

  formatDeltaAbsolute(delta: number): string {
    const formatted = Math.abs(delta).toLocaleString();
    return delta >= 0 ? `+${formatted}` : `-${formatted}`;
  }

  formatDeltaPercent(delta: number, oldVal: any): string {
    const o = parseFloat(String(oldVal));
    if (!isNaN(o) && o !== 0) {
      const pct = (delta / Math.abs(o)) * 100;
      return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
    }
    return '';
  }

  getDeltaClass(delta: number | null): string {
    if (delta === null) return '';
    if (delta < 0) return 'delta-negative';
    if (delta > 0) return 'delta-positive';
    return 'delta-neutral';
  }

  hasDelta(oldVal: any, newVal: any): boolean {
    return this.calculateDelta(oldVal, newVal) !== null;
  }

  getDeltaInfo(oldVal: any, newVal: any): { value: number } | null {
    const d = this.calculateDelta(oldVal, newVal);
    return d !== null ? { value: d } : null;
  }

  // ==================== String Diff ====================

  private isStringField(oldVal: any, newVal: any): boolean {
    return typeof oldVal === 'string' && typeof newVal === 'string'
      && isNaN(parseFloat(oldVal)) && isNaN(parseFloat(newVal));
  }

  getStringDiff(oldVal: any, newVal: any): StringDiff | null {
    if (!this.isStringField(oldVal, newVal)) return null;
    const o = oldVal as string;
    const n = newVal as string;
    if (o === n) return null;
    // Bail out on very long strings — inline diff becomes noisy
    if (o.length > 120 || n.length > 120) return null;

    let prefixLen = 0;
    while (prefixLen < o.length && prefixLen < n.length && o[prefixLen] === n[prefixLen]) {
      prefixLen++;
    }
    let oEnd = o.length;
    let nEnd = n.length;
    while (oEnd > prefixLen && nEnd > prefixLen && o[oEnd - 1] === n[nEnd - 1]) {
      oEnd--;
      nEnd--;
    }
    return {
      prefix: o.slice(0, prefixLen),
      removed: o.slice(prefixLen, oEnd),
      added: n.slice(prefixLen, nEnd),
      suffix: o.slice(oEnd),
    };
  }

  getStringDiffSummary(oldVal: any, newVal: any): string | null {
    const diff = this.getStringDiff(oldVal, newVal);
    if (!diff) return null;
    const { prefix, removed, added, suffix } = diff;
    const q = (s: string) => `"${s}"`;

    if (!removed && added) {
      if (!prefix) return `Added prefix ${q(added)}`;
      if (!suffix) return `Added trailing ${q(added)}`;
      return `Inserted ${q(added)}`;
    }
    if (removed && !added) {
      if (!prefix) return `Removed prefix ${q(removed)}`;
      if (!suffix) return `Removed trailing ${q(removed)}`;
      return `Removed ${q(removed)}`;
    }
    if (!prefix && !suffix) {
      return `Replaced ${q(removed)} with ${q(added)}`;
    }
    if (removed.toLowerCase() === added.toLowerCase()) {
      return `Casing changed: ${q(removed)} → ${q(added)}`;
    }
    return `Changed ${q(removed)} → ${q(added)}`;
  }

  // ==================== Formatting ====================

  formatValue(value: any): string {
    if (value === null || value === undefined) return '(null)';
    if (Array.isArray(value)) return JSON.stringify(value);
    if (typeof value === 'object') return JSON.stringify(value, null, 0);
    if (typeof value === 'string') return `"${value}"`;
    return String(value);
  }

  formatValueShort(value: any): string {
    const s = this.formatValue(value);
    return s.length > 40 ? s.slice(0, 37) + '…' : s;
  }

  async copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      this.copySuccess = true;
      setTimeout(() => (this.copySuccess = false), 2000);
    } catch {
      // ignore — clipboard not available
    }
  }

  // ==================== Navigation ====================

  navigateToRecord(tableName: string, recordId: string): void {
    const routeName = this.getRouteNameForTable(tableName);
    const url = this.router.createUrlTree([`/${routeName}/${recordId}`]);
    window.open(url.toString(), '_blank');
  }

  private getRouteNameForTable(tableName: string): string {
    const map: Record<string, string> = {
      byobs: 'byob',
      items: 'items',
      collections: 'collections',
      challenges: 'challenges',
      inboxMessages: 'inbox-messages',
      loadingScreens: 'loading-screens',
      dailyRewards: 'daily-rewards',
      seasonPass: 'season-pass',
      perks: 'perks',
      achievements: 'achievements',
      vendors: 'vendors',
      titles: 'titles',
      sponsors: 'sponsors',
      nurture: 'nurture',
      currencies: 'currencies',
      climates: 'climates',
      categories: 'categories',
      batches: 'batches',
    };
    return map[tableName] || tableName;
  }

  // ==================== Row computation ====================

  private computeRecordDetailsForTable(tableName: string): any[] {
    const table = this.tableOverview.find(t => t.name === tableName);
    if (!table) return [];

    const rows: any[] = [];

    (table.updatedRows || []).forEach((row: any) => {
      if (row.id != null && row.changes) {
        Object.keys(row.changes).forEach(fieldName => {
          const fc = row.changes[fieldName];
          const sd = this.getStringDiff(fc.old, fc.new);
          rows.push({
            recordId: row.id, fieldName, oldValue: fc.old, newValue: fc.new, changeType: 'updated',
            stringDiff: sd,
            stringDiffSummary: sd ? this.getStringDiffSummary(fc.old, fc.new) : null,
          });
        });
      }
    });

    (table.addedRows || []).forEach((row: any) => {
      if (row.id != null && row.data) {
        Object.keys(row.data).forEach(fieldName => {
          rows.push({ recordId: row.id, fieldName, oldValue: null, newValue: row.data[fieldName], changeType: 'added' });
        });
      }
    });

    (table.deletedRows || []).forEach((row: any) => {
      if (row.id != null && row.data) {
        Object.keys(row.data).forEach(fieldName => {
          rows.push({ recordId: row.id, fieldName, oldValue: row.data[fieldName], newValue: null, changeType: 'deleted' });
        });
      }
    });

    return rows.sort((a, b) =>
      String(a.recordId).localeCompare(String(b.recordId)) || a.fieldName.localeCompare(b.fieldName),
    );
  }

  // ==================== Export ====================

  toggleRawData(): void {
    this.showRawData = !this.showRawData;
  }

  exportToSQLite(): void {
    if (!this.selectedDiff) return;
    this.gamedataDownloadService.downloadWithPrompt(
      this.selectedDiff.buildVersion,
      this.selectedDiff.environment || 'unknown',
    );
  }

  exportToCSV(): void {
    if (!this.selectedDiff) return;
    try {
      const csvData: any[] = [];
      csvData.push({
        'Type': 'SUMMARY',
        'Build Version': this.selectedDiff.buildVersion || '',
        'Environment': this.selectedDiff.environment || '',
        'Generated At': this.selectedDiff.generatedAt || this.selectedDiff.createdAt || '',
        'Table Name': 'ALL TABLES',
        'Record ID': '',
        'Field Name': '',
        'Old Value': '',
        'New Value': '',
        'Change Type': '',
        'Tables Changed': this.statsData.tablesChanged,
        'Records Created': this.statsData.recordsCreated,
        'Records Updated': this.statsData.recordsUpdated,
        'Records Deleted': this.statsData.recordsDeleted,
      });

      this.tableOverview.forEach(table => {
        const changes = this.recordDetailsCache.get(table.name) ?? this.computeRecordDetailsForTable(table.name);
        if (changes.length === 0) {
          csvData.push({
            'Type': 'TABLE', 'Build Version': this.selectedDiff.buildVersion || '',
            'Environment': this.selectedDiff.environment || '',
            'Generated At': this.selectedDiff.generatedAt || this.selectedDiff.createdAt || '',
            'Table Name': table.name, 'Record ID': '', 'Field Name': '', 'Old Value': '', 'New Value': '',
            'Change Type': '', 'Tables Changed': '', 'Records Created': table.createdCount,
            'Records Updated': table.editedCount, 'Records Deleted': table.deletedCount,
          });
        } else {
          changes.forEach(ch => {
            csvData.push({
              'Type': 'RECORD_CHANGE', 'Build Version': this.selectedDiff.buildVersion || '',
              'Environment': this.selectedDiff.environment || '',
              'Generated At': this.selectedDiff.generatedAt || this.selectedDiff.createdAt || '',
              'Table Name': table.name, 'Record ID': ch.recordId, 'Field Name': ch.fieldName,
              'Old Value': this.formatValue(ch.oldValue), 'New Value': this.formatValue(ch.newValue),
              'Change Type': ch.changeType || 'updated', 'Tables Changed': '', 'Records Created': '',
              'Records Updated': '', 'Records Deleted': '',
            });
          });
        }
      });

      const headers = Object.keys(csvData[0] || {});
      const rows = csvData.map(row => headers.map(h => `"${row[h] ?? ''}"`).join(','));
      const content = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.setAttribute('href', URL.createObjectURL(blob));
      link.setAttribute('download', `gamedata-diff-${this.selectedDiff.buildVersion}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      this.logger.error('Error exporting CSV:', error);
    }
  }

  trackByTableName(_i: number, t: { name: string }): string { return t.name; }
  trackByChangeId(_i: number, c: { recordId: any; fieldName: string }): string { return `${c.recordId}-${c.fieldName}`; }
}
